import React from 'react';
import {gql} from '@apollo/client';
import {EzModal} from '@ezcater/recipe';
import FCWithQuery from '@src/util/FCWithQuery';
import {EzManageNavSupportOptionsConstants} from '@src/__generated__/EzManageNavSupportOptionsConstants';
import {useTranslation} from 'react-i18next';
import Chat from '@src/icons/Chat';
import Phone from '@src/icons/Phone';
import Help from '@src/icons/Help';
import Email from '@src/icons/Email';
import {HELP_CENTER_URL} from '../../EzManageNav/paths';
import {ClickableContactRow, StaticContactRow} from './ContactRow';

interface SupportOptionsModalProps {
  constants: EzManageNavSupportOptionsConstants | null;
  dismissModal: () => void;
  liveChatEnabled: boolean;
  openChat: () => void;
  switchToEmailModal: () => void;
}

const SupportOptionsModal: FCWithQuery<SupportOptionsModalProps> = {
  Component: ({constants, dismissModal, liveChatEnabled, openChat, switchToEmailModal}) => {
    const {t: useMessage} = useTranslation();
    const dismissLabel = useMessage('components.SupportModal.SupportOptionsModal.dismiss');
    const header = useMessage('components.SupportModal.SupportOptionsModal.header');
    const phoneHeader = useMessage('components.SupportModal.SupportOptionsModal.phone.header');
    const chatHeader = useMessage('components.SupportModal.SupportOptionsModal.chat.header');
    const chatSubText = useMessage('components.SupportModal.SupportOptionsModal.chat.subText');
    const helpHeader = useMessage('components.SupportModal.SupportOptionsModal.help.header');
    const helpSubText = useMessage('components.SupportModal.SupportOptionsModal.help.subText');
    const emailHeader = useMessage('components.SupportModal.SupportOptionsModal.email.header');
    const emailSubText = useMessage('components.SupportModal.SupportOptionsModal.email.subText');
    const {catererSupportPhoneNumber} = (constants && constants.ezCaterCompanyInfo) || {};

    const handleClickChat = (event: React.MouseEvent<HTMLElement>) => {
      openChat();
      event.preventDefault();
    };

    const handleEmailClicked = (event: React.MouseEvent<HTMLElement>) => {
      switchToEmailModal();
      event.preventDefault();
    };

    return (
      <EzModal dismissLabel={dismissLabel} onDismiss={dismissModal} headerText={header} isOpen>
        <StaticContactRow
          heading={phoneHeader}
          hideMobile
          Icon={Phone}
          subText={catererSupportPhoneNumber || ''}
        />
        <ClickableContactRow
          heading={phoneHeader}
          hideDesktop
          href={`tel:${catererSupportPhoneNumber || ''}`}
          Icon={Phone}
          subText={catererSupportPhoneNumber || ''}
        />
        {liveChatEnabled && (
          <ClickableContactRow
            heading={chatHeader}
            hideMobile
            Icon={Chat}
            onClick={handleClickChat}
            subText={chatSubText}
          />
        )}
        <ClickableContactRow
          heading={helpHeader}
          href={HELP_CENTER_URL}
          Icon={Help}
          subText={helpSubText}
          target="_blank"
        />
        <ClickableContactRow
          heading={emailHeader}
          Icon={Email}
          onClick={handleEmailClicked}
          subText={emailSubText}
        />
      </EzModal>
    );
  },
  fragment: gql`
    fragment EzManageNavSupportOptionsConstants on Constants {
      ezCaterCompanyInfo {
        catererSupportPhoneNumber
      }
    }
  `,
};

export default SupportOptionsModal;
